import React, { useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { IconButton } from '@mui/material';

import { HintProps } from './types';
import {
  EditButtons,
  ExpandedHintText,
  HintContainer,
  IconOnlyWrapper,
  StyledInfoIcon,
  StyledText,
  StyledTextArea,
} from './styles';
import { useI18n } from '../../../hooks/useI18n';
import { TranslationKeys } from '../../../i18n/locales/defaultResources';

const Hint: React.FC<
  HintProps & { withEditButtons?: boolean; readonlyHint?: boolean }
> = ({
  text,
  className,
  isCreatingMode = false,
  onSave,
  onClear,
  disabled = false,
  withEditButtons = false,
  readonlyHint = false,
}) => {
  const [inputValue, setInputValue] = useState(text);
  const [isHintVisible, setIsHintVisible] = useState(false);
  const { t } = useI18n();
  const handleSave = () => {
    if (withEditButtons) {
      onSave?.(inputValue);
    }
  };

  const handleClear = () => {
    if (withEditButtons) {
      setInputValue('');
      onClear?.();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (!withEditButtons) {
      onSave?.(newValue);
    }
  };

  const handleToggleHint = () => {
    if (readonlyHint) {
      setIsHintVisible(!isHintVisible);
    }
  };

  if (isCreatingMode) {
    return (
      <HintContainer className={className}>
        <StyledInfoIcon className="small" />
        <StyledTextArea
          value={inputValue}
          onChange={handleChange}
          placeholder={t(TranslationKeys.hint_input_placeholder) as string}
          disabled={disabled}
        />
        {withEditButtons && (
          <EditButtons>
            <IconButton size="small" onClick={handleSave}>
              <CheckIcon className="save-icon" />
            </IconButton>
            <IconButton size="small" onClick={handleClear}>
              <ClearIcon className="clear-icon" />
            </IconButton>
          </EditButtons>
        )}
      </HintContainer>
    );
  }

  if (!isHintVisible && readonlyHint) {
    return (
      <IconOnlyWrapper onClick={handleToggleHint}>
        <StyledInfoIcon className="clickable" />
        <StyledText variant="body2">
          {t(TranslationKeys.hint_more_information)}
        </StyledText>
      </IconOnlyWrapper>
    );
  }

  return (
    <HintContainer className={className}>
      <StyledInfoIcon
        onClick={handleToggleHint}
        className={`small ${readonlyHint ? 'clickable' : ''}`}
      />
      <ExpandedHintText variant="body2">{text}</ExpandedHintText>
    </HintContainer>
  );
};

export default Hint;
